/**
 * CooperMembresManagement.js
 * Composant de gestion des membres pour les COOPER'S
 * Utilise le formulaire professionnel MembreCreationForm (copie conforme de DEVENIR MEMBRE)
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Badge } from '../ui/badge';

import { CheckCircle, Clock, XCircle, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useMemberManagementConfig } from '../../hooks/useMemberManagementConfig';

import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// V37.22 — Parser réutilisable pour l'import depuis presse-papier
const IMPORT_KEY_MAP = {
  'raison sociale': 'raison_sociale',
  'siret': 'siret',
  'prenom': 'prenom',
  'prénom': 'prenom',
  'nom': 'nom',
  'email': 'email',
  'telephone': 'telephone',
  'téléphone': 'telephone',
  'adresse': 'adresse',
  'code postal': 'code_postal',
  'ville': 'ville',
  'territoire': 'territoire',
  'statut juridique': 'statut_juridique',
};

// Champs critiques affichés en chips de validation (ordre = priorité)
const _IMPORT_CRITICAL_FIELDS = [
  { key: 'raison_sociale', label: 'Raison sociale' },
  { key: 'nom', label: 'Nom' },
  { key: 'prenom', label: 'Prénom' },
  { key: 'email', label: 'Email' },
  { key: 'telephone', label: 'Téléphone' },
  { key: 'territoire', label: 'Territoire' },
];

function parseImportText(text) {
  const out = {};
  (text || '').split(/\r?\n/).forEach((line) => {
    const idx = line.indexOf(':');
    if (idx <= 0) return;
    const rawKey = line.slice(0, idx).trim().toLowerCase();
    const value = line.slice(idx + 1).trim();
    if (!value) return;
    const mapped = IMPORT_KEY_MAP[rawKey];
    if (mapped) out[mapped] = value;
  });
  return out;
}

export function useCooperMembres() {
  const { getAuthHeader, user } = useAuth();
  const roleConfig = useMemberManagementConfig(user?.role);
  const navigate = useNavigate();
  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  // V37.20 — Import depuis presse-papier
  const [showImportModal, setShowImportModal] = useState(false);
  const [importedInitialValues, setImportedInitialValues] = useState(null);
  const [importText, setImportText] = useState('');
  // V37.22 — Auto-parsing en temps réel (recalcule à chaque frappe / collage)
  const importParsed = useMemo(() => parseImportText(importText), [importText]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEstimationModal, setShowEstimationModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showFTPEModal, setShowFTPEModal] = useState(false);
  const [showSimulateurModal, setShowSimulateurModal] = useState(false);
  const [ftpeLoading, setFtpeLoading] = useState(false);
  
  // Data
  const [selectedMembre, setSelectedMembre] = useState(null);
  const [membreDocuments, setMembreDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  
  // Estimation
  const [newEstimation, setNewEstimation] = useState({
    titre: '',
    description: '',
    montant_ht: '',
    taux_tva: 20,
    validite_jours: 30
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    actifs: 0,
    en_attente: 0,
    estimations_envoyees: 0
  });

  useEffect(() => {
    fetchMembres();
  }, []);

  const fetchMembres = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cooper/membres/liste`, {
        headers: getAuthHeader()
      });
      
      // Trier du plus récent au plus ancien
      const membresData = (response.data.membres || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setMembres(membresData);
      
      // Calculer les stats
      setStats({
        total: membresData.length,
        actifs: membresData.filter(m => m.status === 'actif').length,
        en_attente: membresData.filter(m => m.status === 'en_attente_connexion').length,
        estimations_envoyees: membresData.reduce((acc, m) => acc + (m.estimations_count || 0), 0)
      });
    } catch (error) {
      if (error.response?.status === 404) {
        setMembres([]);
        setStats({ total: 0, actifs: 0, en_attente: 0, estimations_envoyees: 0 });
      } else {
        toast.error('Erreur lors du chargement des membres');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewMembre = (membre) => {
    setSelectedMembre(membre);
    setMembreDocuments(null); // Reset documents
    setShowDetailModal(true);
  };

  // Charger les documents et simulations d'un membre
  const fetchMembreDocuments = async (membreId) => {
    setLoadingDocuments(true);
    try {
      const response = await axios.get(
        `${API}/cooper/membres/${membreId}/mes-documents`,
        { headers: getAuthHeader() }
      );
      // Adapter les données du nouvel endpoint pour l'ancien format
      const data = response.data;
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      // ========================================
      // FILTRE CRITIQUE: Document réellement disponible
      // Un document est disponible UNIQUEMENT s'il a une URL valide
      // ========================================
      const hasValidUrl = (doc) => {
        const url = doc.url || doc.download_url || doc.file_url || doc.path;
        return url && typeof url === 'string' && url.trim().length > 0;
      };
      
      // Helper pour vérifier si une date d'échéance est valide (non expirée)
      const _isNotExpired = (doc) => {
        const expDate = doc.date_expiration || doc.date_fin || doc.periode_adhesion_fin;
        if (!expDate) return true;
        try {
          let expDt;
          if (typeof expDate === 'string') {
            if (expDate.includes('/')) {
              const [d, m, y] = expDate.split('/');
              expDt = new Date(y, m - 1, d);
            } else {
              expDt = new Date(expDate);
            }
          } else {
            expDt = new Date(expDate);
          }
          return expDt >= now;
        } catch {
          return true;
        }
      };
      
      // Helper pour vérifier si un document est dans les 30 derniers jours
      const isWithin30Days = (doc) => {
        const createdAt = doc.date_creation || doc.created_at;
        if (!createdAt) return true;
        try {
          const createdDt = new Date(createdAt);
          return createdDt >= thirtyDaysAgo;
        } catch {
          return true;
        }
      };
      
      // Deduplication: si une version signee admin existe, masquer la version systeme
      const adminDocs = (data.documents_admin || []);
      const adminTypes = new Set(adminDocs.map(d => d.type));
      const ADMIN_SUPERSEDES = {
        'attestation_signee': ['attestation_rcds'],
        'conditions_generales_particulieres_signees': ['conditions_generales'],
        'attestation_ftpe_signee': ['attestation_ftpe'],
        'devis_ess': ['devis_ess'],
      };
      const supersededTypes = new Set();
      for (const [adminType, sysTypes] of Object.entries(ADMIN_SUPERSEDES)) {
        if (adminTypes.has(adminType)) sysTypes.forEach(t => supersededTypes.add(t));
      }
      const systemDocs = (data.documents || []).filter(d => !supersededTypes.has(d.type));

      // 1. Simulations en cours - affichage 30 jours max
      const simulations_en_cours = (data.souscriptions_en_cours || []).filter(sim => {
        return isWithin30Days(sim) && sim.jours_restants > 0;
      });
      
      // 2. Attestations FTPE (ne pas filtrer par hasValidUrl car les non-signees n'ont pas toujours d'URL)
      const attestations_ftpe = systemDocs.filter(d => {
        const isFtpe = d.type?.toLowerCase().includes('ftpe') || 
                       d.type?.toLowerCase().includes('attestation_ftpe');
        return isFtpe;
      });
      
      // 3. Attestations RCDS (contrats signés)
      const attestations_rcds = systemDocs.filter(d => {
        const isRcds = d.type?.toLowerCase().includes('attestation') && 
                       d.type?.toLowerCase().includes('rcds');
        return isRcds && hasValidUrl(d);
      });
      
      // 4. Documents contractuels
      const documents_contractuels = systemDocs.filter(d => {
        const isContractuel = ['conditions_generales', 'devis_ess', 'devis_ess_genere', 'proposition', 'avenant', 'attestation_avenant', 'recu_avenant'].includes(d.type);
        return isContractuel && hasValidUrl(d);
      });
      
      // 4b. Certificats d'adhesion O'SCOP
      const certificats_adhesion = systemDocs.filter(d =>
        d.type === 'certificat_adhesion' || d.type === 'membre_adhesion' || d.type === 'certificat'
      );

      // 5. Tous les documents
      const all_documents = systemDocs.filter(hasValidUrl);
      
      // 6. Reçus de paiements
      const recus_valides = (data.recus_paiements || []).filter(hasValidUrl);
      
      // 7. Documents GED
      const ged_valides = (data.documents_ged || []).filter(hasValidUrl);
      
      // O'SCOP: afficher uniquement si FTPE signee existe
      const hasFtpeSignee = data.has_ftpe_signee || attestations_ftpe.some(d => d.document_status === 'signee');
      
      setMembreDocuments({
        documents: all_documents,
        souscriptions_en_cours: simulations_en_cours,
        recus_paiements: recus_valides,
        categories: data.categories || {},
        simulations_en_cours,
        certificats_adhesion: hasFtpeSignee ? certificats_adhesion : [],
        devis_legalcoop: systemDocs.filter((d) => d.type === 'devis_legalcoop'),
        attestations_rcds_signees: attestations_rcds,
        attestations_ftpe_signees: attestations_ftpe,
        documents_contractuels,
        documents_ged: ged_valides,
        documents_admin: adminDocs,
        has_ftpe_signee: hasFtpeSignee,
        soldes_dus: data.soldes_dus || []
      });
    } catch {
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Ouvrir la modal Documents
  const handleViewDocuments = async (membre) => {
    setSelectedMembre(membre);
    setShowDocumentsModal(true);
    await fetchMembreDocuments(membre.id);
  };

  // Initier l'adhésion FTPE pour un membre - Redirige vers le workflow complet
  const handleFTPEAdhesion = (membre) => {
    // Si le membre a déjà une adhésion FTPE active, afficher un message
    if (membre.adhesion_ftpe) {
      toast.info('Ce membre a déjà une adhésion FTPE active');
      return;
    }
    
    // Si une adhésion est en cours, proposer de voir le statut ou reprendre
    if (membre.adhesion_ftpe_en_cours) {
      toast.info('Une demande d\'adhésion FTPE est déjà en cours pour ce membre', {
        action: {
          label: 'Voir',
          onClick: () => navigate(`/adhesion-ftpe-workflow?adhesion_id=${membre.adhesion_ftpe_id || ''}`)
        }
      });
      return;
    }
    
    // Construire l'URL avec les paramètres du membre pour pré-remplir le formulaire
    const params = new URLSearchParams({
      for_member: 'true',
      member_id: membre.id || '',
      member_name: `${membre.prenom || ''} ${membre.nom || ''}`.trim(),
      member_email: membre.email || '',
      member_phone: membre.telephone || '',
      member_entreprise: membre.entreprise || membre.raison_sociale || '',
      member_territoire: membre.territoire || 'Guadeloupe'
    });
    
    // Naviguer vers le workflow d'adhésion FTPE avec les données pré-remplies
    navigate(`/adhesion-ftpe-workflow?${params.toString()}`);
  };

  // Créer l'adhésion FTPE - redirige vers le workflow complet
  const handleCreateFTPEAdhesion = () => {
    if (!selectedMembre) return;
    
    setShowFTPEModal(false);
    setShowDetailModal(false);
    
    // Construire l'URL avec les paramètres du membre pour pré-remplir le formulaire
    const params = new URLSearchParams({
      for_member: 'true',
      member_id: selectedMembre.id || '',
      member_name: `${selectedMembre.prenom || ''} ${selectedMembre.nom || ''}`.trim(),
      member_email: selectedMembre.email || '',
      member_phone: selectedMembre.telephone || '',
      member_entreprise: selectedMembre.entreprise || selectedMembre.raison_sociale || '',
      member_territoire: selectedMembre.territoire || 'Guadeloupe'
    });
    
    // Naviguer vers le workflow d'adhésion FTPE avec les données pré-remplies
    navigate(`/adhesion-ftpe-workflow?${params.toString()}`);
  };
  
  // Reprendre une simulation en cours
  const handleReprendreSimulation = (simulation) => {
    const souscriptionId = simulation.souscription_id || simulation.reference || simulation.id;
    
    // Vérifier si c'est une simulation DOMIUM
    if (simulation.type === 'simulation_domium' || simulation.source === 'domium_subscriptions') {
      // Rediriger vers le simulateur DOS DOMIUM avec le paramètre resume
      window.location.href = `/simulateur-dos?resume=${souscriptionId}`;
    } else {
      // Rediriger vers le workflow de souscription annuelle avec le paramètre resume
      window.location.href = `/souscription-annuelle?resume=${souscriptionId}`;
    }
  };

  const handleCreateEstimation = async () => {
    if (!newEstimation.titre || !newEstimation.montant_ht) {
      toast.error('Veuillez remplir le titre et le montant');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/cooper/membres/estimations/creer`,
        {
          membre_id: selectedMembre.id,
          ...newEstimation,
          montant_ht: parseFloat(newEstimation.montant_ht)
        },
        { headers: getAuthHeader() }
      );

      toast.success('Estimation créée avec succès !');
      setShowEstimationModal(false);
      setNewEstimation({
        titre: '',
        description: '',
        montant_ht: '',
        taux_tva: 20,
        validite_jours: 30
      });
      
      if (window.confirm('Voulez-vous envoyer cette estimation au membre maintenant ?')) {
        await handleSendEstimation(response.data.estimation.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleSendEstimation = async (estimationId) => {
    try {
      await axios.post(
        `${API}/cooper/membres/estimations/${estimationId}/envoyer`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Estimation envoyée au membre !');
    } catch {
      toast.error("Erreur lors de l'envoi");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'actif':
        return <Badge className="bg-green-600 text-white font-semibold"><CheckCircle className="h-3 w-3 mr-1" /> Actif</Badge>;
      case 'en_attente_connexion':
        return <Badge className="bg-orange-500 font-semibold"><Clock className="h-3 w-3 mr-1" /> En attente</Badge>;
      case 'bloque':
        return <Badge className="bg-red-600 text-white font-semibold"><XCircle className="h-3 w-3 mr-1" /> Bloqué</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Fonction pour obtenir le badge FTPE avec gestion de l'expiration
  const getFTPEBadge = (membre) => {
    // Vérifier si l'adhésion est active
    if (!membre.adhesion_ftpe && !membre.ftpe_status) {
      // En cours de demande
      if (membre.adhesion_ftpe_en_cours) {
        return (
          <Badge className="bg-orange-600 text-white border border-orange-800 font-semibold">
            <Clock className="h-3 w-3 mr-1" />
            En cours
          </Badge>
        );
      }
      // Non adhérent
      return (
        <Badge variant="outline" className="text-gray-500">
          Non adhérent
        </Badge>
      );
    }
    
    // Vérifier la date d'expiration
    const expirationDate = membre.ftpe_expiration_date;
    if (expirationDate) {
      const now = new Date();
      const expDate = new Date(expirationDate);
      const daysUntilExpiration = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiration < 0) {
        // Expiré - À renouveler
        return (
          <Badge className="bg-red-700 text-white border border-red-900 font-semibold">
            <AlertTriangle className="h-3 w-3 mr-1" />
            À renouveler
          </Badge>
        );
      } else if (daysUntilExpiration <= 30) {
        // Expire bientôt
        return (
          <Badge className="bg-amber-600 text-white border border-amber-800 font-semibold">
            <Clock className="h-3 w-3 mr-1" />
            Expire dans {daysUntilExpiration}j
          </Badge>
        );
      }
    }
    
    // Adhérent actif
    if (membre.adhesion_ftpe || membre.ftpe_status === 'active') {
      return (
        <Badge className="bg-green-700 text-white border border-green-900 font-semibold">
          <CheckCircle className="h-3 w-3 mr-1" />
          Adhérent
        </Badge>
      );
    }
    
    // Status pending
    if (membre.ftpe_status === 'pending') {
      return (
        <Badge className="bg-orange-600 text-white border border-orange-800 font-semibold">
          <Clock className="h-3 w-3 mr-1" />
          En cours
        </Badge>
      );
    }
    
    // Non adhérent par défaut
    return (
      <Badge variant="outline" className="text-gray-500">
        Non adhérent
      </Badge>
    );
  };

  // Liste des simulateurs disponibles (provient du hook role-aware partage)
  const simulateursDisponibles = roleConfig.simulators;

  // Ouvrir un simulateur avec les données du membre pré-remplies
  const handleOpenSimulateur = (simulateur) => {
    if (!selectedMembre) return;
    
    // Construire les paramètres de pré-remplissage
    const params = new URLSearchParams({
      prefill: 'true',
      membre_id: selectedMembre.id || '',
      raison_sociale: selectedMembre.entreprise || selectedMembre.raison_sociale || `${selectedMembre.prenom} ${selectedMembre.nom}`,
      siret: selectedMembre.siret || '',
      nom: selectedMembre.nom || '',
      prenom: selectedMembre.prenom || '',
      email: selectedMembre.email || '',
      telephone: selectedMembre.telephone || '',
      adresse: selectedMembre.adresse || '',
      code_postal: selectedMembre.code_postal || '',
      ville: selectedMembre.ville || '',
      statut_juridique: selectedMembre.statut_juridique || selectedMembre.forme_juridique || '',
      territoire: selectedMembre.territoire || 'Guadeloupe',
      activite: selectedMembre.activite || ''
    });
    
    // Naviguer vers le simulateur avec les paramètres
    if (simulateur.ia) {
      params.set('ia_membre', selectedMembre.id || '');
      params.set('from', 'ia_activites');
    }
    const url = `${simulateur.path}?${params.toString()}`;
    window.open(url, '_blank');
    
    setShowSimulateurModal(false);
    toast.success(`Simulateur ${simulateur.nom} ouvert avec les données de ${selectedMembre.prenom} ${selectedMembre.nom}`);
  };
  return { fetchMembreDocuments, fetchMembres, ftpeLoading, getAuthHeader, getFTPEBadge, getStatusBadge, handleCreateEstimation, handleCreateFTPEAdhesion, handleFTPEAdhesion, handleOpenSimulateur, handleReprendreSimulation, handleSendEstimation, handleViewDocuments, handleViewMembre, importParsed, importText, importedInitialValues, loading, loadingDocuments, membreDocuments, membres, navigate, newEstimation, roleConfig, selectedMembre, setFtpeLoading, setImportText, setImportedInitialValues, setLoading, setLoadingDocuments, setMembreDocuments, setMembres, setNewEstimation, setSelectedMembre, setShowCreateModal, setShowDetailModal, setShowDocumentsModal, setShowEstimationModal, setShowFTPEModal, setShowImportModal, setShowSimulateurModal, setStats, showCreateModal, showDetailModal, showDocumentsModal, showEstimationModal, showFTPEModal, showImportModal, showSimulateurModal, simulateursDisponibles, stats, user };
}
